import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Badge, Button, EmptyState, Modal, Skeleton } from '../../../ui';
import { toast } from '../../../ui/toast';
import { apiErrorMessage } from '../../../utils/apiError';
import { useAgentProposal, useDecideProposal, type Decision } from '../hooks/useAgentProposals';
import { STATUS_LABELS, STATUS_TONES, actionLabel, agentLabel, formatDateTime } from '../labels';
import type { AgentProposal } from '../services/agent-proposals.service';

function notifyOutcome(item: AgentProposal): void {
  if (item.status === 'EXECUTED') {
    toast.success('Proposta aprovada e executada');
    return;
  }
  if (item.status === 'FAILED') {
    toast.error(`A execução falhou: ${item.error ?? 'motivo não informado'}`);
    return;
  }
  if (item.status === 'REJECTED') {
    toast.success('Proposta recusada');
  }
}

const backLink = (
  <Link to="/aprovacoes" className="text-sm text-accent">
    Voltar às aprovações
  </Link>
);

export function AprovacaoDetailPage() {
  const { id: rawId } = useParams();
  const id = Number(rawId);
  const query = useAgentProposal(id);
  const decide = useDecideProposal();
  const [decision, setDecision] = useState<Decision | null>(null);
  const [note, setNote] = useState('');
  const [desmarcados, setDesmarcados] = useState<Set<string>>(new Set());

  if (!Number.isInteger(id) || id <= 0) {
    return (
      <EmptyState
        title="Proposta não encontrada"
        body="O endereço não tem um número de proposta válido."
        action={backLink}
      />
    );
  }
  if (query.isPending) {
    return <Skeleton height={320} />;
  }
  if (query.isError) {
    return (
      <EmptyState
        title="Proposta não encontrada"
        body={apiErrorMessage(query.error, 'Não foi possível carregar a proposta')}
        action={backLink}
      />
    );
  }

  const p = query.data;
  const expired = p.status === 'PENDING' && new Date(p.expiresAt).getTime() <= Date.now();
  const canDecide = p.status === 'PENDING' && !expired;
  const itens = p.preview.items ?? [];
  const foraDaExecucao = canDecide ? desmarcados : new Set(p.excludedItems ?? []);
  const marcados = itens.filter((i) => !foraDaExecucao.has(i.key)).length;
  const nadaMarcado = itens.length > 0 && marcados === 0;

  function alternar(key: string): void {
    setDesmarcados((atual) => {
      const novo = new Set(atual);
      if (novo.has(key)) {
        novo.delete(key);
      } else {
        novo.add(key);
      }
      return novo;
    });
  }

  function confirmDecision(): void {
    if (decision === null) {
      return;
    }
    const trimmed = note.trim();
    decide.mutate(
      {
        id: p.id,
        decision,
        note: trimmed === '' ? undefined : trimmed,
        excludedItems:
          decision === 'approve' && desmarcados.size > 0 ? [...desmarcados] : undefined,
      },
      {
        onSuccess: (item) => {
          setDecision(null);
          setNote('');
          setDesmarcados(new Set());
          notifyOutcome(item);
        },
        onError: (error) => {
          toast.error(apiErrorMessage(error, 'Não foi possível registrar a decisão'));
        },
      }
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <Link
        to="/aprovacoes"
        className="inline-flex items-center gap-1 text-sm text-ink-secondary hover:text-ink-primary"
      >
        <ArrowLeft className="w-4 h-4" aria-hidden /> Aprovações
      </Link>

      <header className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={STATUS_TONES[p.status]}>
            {expired ? 'Vencida' : STATUS_LABELS[p.status]}
          </Badge>
          <span className="text-xs text-ink-tertiary">
            #{p.id} · {agentLabel(p.agent)} · {actionLabel(p.actionType)}
          </span>
        </div>
        <h1 className="font-serif text-2xl font-semibold text-ink-primary">{p.preview.title}</h1>
        <p className="text-xs text-ink-tertiary">
          Criada em {formatDateTime(p.createdAt)} · vence em {formatDateTime(p.expiresAt)}
        </p>
      </header>

      <section className="card flex flex-col gap-2">
        <h2 className="font-medium text-ink-primary">O que o agente diz</h2>
        <p className="whitespace-pre-wrap text-sm text-ink-secondary">{p.summary}</p>
      </section>

      <section className="card flex flex-col gap-2">
        <h2 className="font-medium text-ink-primary">O que o CRM calculou</h2>
        <dl className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-[max-content_1fr]">
          {p.preview.details.map((d, i) => (
            <div key={`${i}-${d.label}`} className="contents">
              <dt className="text-xs font-medium text-ink-tertiary">{d.label}</dt>
              <dd className="whitespace-pre-wrap text-sm text-ink-primary">{d.value}</dd>
            </div>
          ))}
        </dl>
      </section>

      {itens.length > 0 && (
        <section className="card flex flex-col gap-2">
          <div className="flex items-baseline justify-between gap-2">
            <h2 className="font-medium text-ink-primary">Itens</h2>
            <span className="text-xs text-ink-tertiary">
              {marcados} de {itens.length} marcados
            </span>
          </div>
          <ul className="flex flex-col gap-2">
            {itens.map((item) => {
              const marcado = !foraDaExecucao.has(item.key);
              return (
                <li key={item.key} className="flex items-start gap-2">
                  <input
                    id={`item-${item.key}`}
                    type="checkbox"
                    className="mt-1"
                    checked={marcado}
                    disabled={!canDecide || decide.isPending}
                    onChange={() => alternar(item.key)}
                  />
                  <label htmlFor={`item-${item.key}`} className="flex flex-col text-sm">
                    <span className="text-ink-primary">{item.label}</span>
                    {item.detail && (
                      <span className="text-xs text-ink-tertiary">{item.detail}</span>
                    )}
                  </label>
                  {!canDecide && !marcado && <Badge tone="neutral">desmarcado</Badge>}
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {p.preview.email && (
        <section className="card flex flex-col gap-2">
          <h2 className="font-medium text-ink-primary">E-mail</h2>
          <p className="text-sm text-ink-secondary">
            Assunto: <strong>{p.preview.email.subject}</strong>
          </p>
          {p.preview.email.recipientCount !== undefined && (
            <p className="text-sm text-ink-secondary">
              Destinatários elegíveis: <strong>{p.preview.email.recipientCount}</strong>
              {p.preview.email.skippedCount
                ? ` · fora da lista: ${p.preview.email.skippedCount}`
                : ''}
            </p>
          )}
          {/* sandbox vazio: o HTML do template não roda script nem navega. */}
          <iframe
            title="Prévia do e-mail"
            sandbox=""
            srcDoc={p.preview.email.html}
            className="h-[480px] w-full rounded-md border border-border bg-white"
          />
        </section>
      )}

      <details className="card">
        <summary className="cursor-pointer text-sm text-ink-secondary">
          Payload enviado pelo agente
        </summary>
        <pre className="mt-2 overflow-x-auto text-xs text-ink-secondary">
          {JSON.stringify(p.payload, null, 2)}
        </pre>
      </details>

      {canDecide && (
        <div className="flex gap-2">
          <Button onClick={() => setDecision('approve')} disabled={nadaMarcado}>
            Aprovar
          </Button>
          <Button variant="secondary" onClick={() => setDecision('reject')}>
            Recusar
          </Button>
        </div>
      )}

      {canDecide && nadaMarcado && (
        <p className="text-sm text-warning-contrast">Desmarque menos itens ou recuse a proposta</p>
      )}

      {expired && (
        <p className="text-sm text-warning-contrast">
          Esta proposta está vencida e não pode mais ser aprovada.
        </p>
      )}

      {p.status !== 'PENDING' && (
        <section className="card flex flex-col gap-1 text-sm">
          <h2 className="font-medium text-ink-primary">Decisão</h2>
          {p.decidedBy && (
            <p className="text-ink-secondary">
              {STATUS_LABELS[p.status]} por {p.decidedBy.name} em {formatDateTime(p.decidedAt)}
            </p>
          )}
          {p.decisionNote && <p className="text-ink-secondary">Motivo: {p.decisionNote}</p>}
          {p.error && <p className="text-danger">Erro: {p.error}</p>}
          {p.result && (
            <pre className="overflow-x-auto text-xs text-ink-secondary">
              {JSON.stringify(p.result, null, 2)}
            </pre>
          )}
        </section>
      )}

      {decision !== null && (
        <Modal
          open
          onClose={() => setDecision(null)}
          title={
            decision === 'approve' ? `Aprovar a proposta #${p.id}?` : `Recusar a proposta #${p.id}?`
          }
          description={
            decision === 'approve'
              ? 'O CRM valida de novo e executa agora, em seu nome.'
              : 'Nada é gravado; o agente vê a recusa e o motivo.'
          }
          size="sm"
          footer={
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => setDecision(null)}
                disabled={decide.isPending}
              >
                Cancelar
              </Button>
              <Button
                variant={decision === 'approve' ? 'primary' : 'danger'}
                onClick={confirmDecision}
                disabled={decide.isPending}
              >
                {decision === 'approve' ? 'Confirmar aprovação' : 'Confirmar recusa'}
              </Button>
            </div>
          }
        >
          {decision === 'approve' && desmarcados.size > 0 && (
            <p className="mb-2 text-sm text-ink-secondary">
              {desmarcados.size} item(ns) desmarcado(s) não serão executados.
            </p>
          )}
          <label
            htmlFor="decision-note"
            className="mb-1 block text-sm font-medium text-ink-primary"
          >
            Motivo (opcional)
          </label>
          <textarea
            id="decision-note"
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm"
            rows={3}
            maxLength={1000}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </Modal>
      )}
    </div>
  );
}
